/**
 * The seam between the arena and whatever is driving the tank.
 *
 * `ArenaScene` reads its input through exactly the members below and constructs
 * its source in one place, so a second input model is a new class and a factory
 * branch rather than a change to the scene, the netcode or the simulation. That
 * is not a happy accident — `docs/mobile-feasibility.md` measured this surface
 * before any touch code was written, precisely to find out how wide the change
 * would be; it was ten members then and is eleven now that the camera zooms.
 *
 * **Nothing here is allowed to widen the wire.** `InputCommand` is three axes
 * bounded to [-1, 1] plus a `fire` flag clamped to `0 | 1`, and a touch client
 * cannot express anything a keyboard cannot *ask for* — an axis at 1 is what
 * holding a key sends, and the simulation integrates through `ACCELERATION` and
 * the hull's own limits either way. If a future source seems to need an angle or
 * a position on the wire, it does not; see `CLAUDE.game.md`'s *The server is
 * authoritative*.
 *
 * **`forward` and `turn` are continuous, and that was a wire change** — made on
 * 2026-09-02, because a thumbstick has a deflection and rounding it to the
 * nearest of three values threw all of it away. The bound did not move: the arena
 * swapped `clampAxis` for the `clampUnit` `turretTurn` already used, which is the
 * precedent rather than a new argument. A keyboard still sends exactly -1, 0 or 1
 * and `stepTank` is byte-identical for those, which `simulation.test.ts` pins.
 */
import type { FormationDensityCode, TeamOrderCode } from '@shared/protocol';
import { handheld } from '@/state/device';

export interface InputSource {
  /** Throttle in [-1, 1]: -1 full reverse, 0 idle, 1 full ahead. */
  readonly forward: number;
  /** Hull traverse in [-1, 1]: -1 full left, 0 straight, 1 full right. */
  readonly turn: number;
  /** 1 while the trigger is held. The server's reload decides what that buys. */
  readonly fire: 0 | 1;
  /**
   * True while the player is *glancing* rather than aiming — the gun's target
   * holds and the view moves as an offset from it.
   */
  readonly orbiting: boolean;
  /** True while the tank is deaf to the player (menu open, battle decided). */
  readonly isSuspended: boolean;
  /**
   * Whether the pointer is captured.
   *
   * A touch source answers `true` and means *there is nothing here that can run
   * out of screen* — which is the honest answer rather than a convenient one.
   * The whole reason this is on the interface is the trap in `input.ts`: unlocked,
   * a mouse pins against the edge of the display, `movementX` stops arriving and
   * the gun dead-ends, reported as "the camera refuses to move further left".
   * A finger has no such edge, so that failure mode is deleted rather than
   * ported, and `aimingBounded` is permanently false.
   */
  readonly pointerLocked: boolean;
  /** True when aiming is bounded by the window rather than free. */
  readonly aimingBounded: boolean;
  readonly lockDiagnostics: {
    locked: boolean;
    wanted: boolean;
    bounded: boolean;
    attempts: number;
    refusals: number;
    error: string | null;
    fullscreen: boolean;
  };
  /** The standing team order, or null when the player has never given one. */
  readonly teamOrder: {
    order: TeamOrderCode;
    density: FormationDensityCode;
    orderSeq: number;
  } | null;
  /** Consumes accumulated view movement. Reading it resets it. */
  takeCameraDelta(): { x: number; y: number };
  /** Consumes accumulated aiming movement. Reading it resets it. */
  takeAimDelta(): number;
  /**
   * Consumes accumulated zoom movement. Reading it resets it.
   *
   * Raw device units — a mouse's wheel `deltaY`, a touch pinch's pixels of
   * separation change — not metres. The scene applies its own per-source gain,
   * the same way it already does for pitch.
   */
  takeZoomDelta(): number;
  setSuspended(suspended: boolean): void;
  attach(target: HTMLElement): void;
  detach(target: HTMLElement): void;
}

/**
 * Forced source, for testing a touch build on a machine that has a mouse.
 *
 * `null` means *ask the device*. It exists because the alternative is testing
 * mobile input only on mobile, and this project's harness runs headless
 * Chromium on a desktop — so without an override the touch path would ship with
 * no automated coverage at all, which is the `RUNNING_GEAR_ENABLED` shape.
 */
let forced: 'touch' | 'keyboard' | null = null;

export function forceInputSource(kind: 'touch' | 'keyboard' | null): void {
  forced = kind;
}

export function forcedInputSource(): 'touch' | 'keyboard' | null {
  return forced;
}

/**
 * Whether this session should be driven by thumbs.
 *
 * **Here rather than beside the factory**, and the reason is the entry chunk.
 * `App.vue` has to know the answer — it asks for fullscreen from inside the
 * gesture that pressed Deploy — and importing it from `make-input.ts` would drag
 * both `InputState` and `TouchInputState` into the bundle that has to paint the
 * landing page. This module is types plus two small functions, so importing it
 * costs the entry graph `device.ts` and nothing else. See `CLAUDE.game.md`'s
 * *The engine is not in the entry chunk*.
 *
 * `handheld()` is the conjunction of a coarse primary pointer, no hover and
 * touch hardware — each signal alone has a known false positive (a touchscreen
 * laptop, a tablet on a trackpad, a high pixel ratio), and it is deliberately not
 * a user-agent sniff. See `state/device.ts`.
 */
export function wantsTouch(): boolean {
  const kind = forcedInputSource();
  if (kind !== null) return kind === 'touch';
  return handheld();
}
